import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Copy, Mail, User, MessageSquare, Loader2, Gift, ShieldCheck, Zap, CreditCard } from 'lucide-react';

interface GutscheinOption {
  value: 15 | 30 | 50;
  label: string;
  benefits: string[];
}

/** Narrows an n8n webhook's JSON body enough to read optional string/number fields safely. */
function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

const gutscheinOptions: GutscheinOption[] = [
  {
    value: 15,
    label: '15€ Gutschein',
    benefits: ['Sofort per E-Mail', 'Flexibel einlösbar'],
  },
  {
    value: 30,
    label: '30€ Gutschein',
    benefits: ['Sofort per E-Mail', 'Flexibel einlösbar'],
  },
  {
    value: 50,
    label: '50€ Gutschein',
    benefits: ['Sofort per E-Mail', 'Flexibel einlösbar'],
  },
];

const GutscheinKaufen: React.FC = () => {
  const [selectedValue, setSelectedValue] = useState<15 | 30 | 50 | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [purchasedCode, setPurchasedCode] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid = selectedValue && name.trim() && email.trim() && isValidEmail(email);

  const handleCopyCode = () => {
    if (purchasedCode) {
      navigator.clipboard.writeText(purchasedCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handlePayPalCheckout = async () => {
    if (!isFormValid) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('https://n8n.cogniiq.co/webhook/gutschein paypal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          kind: 'giftcard',
          amount: selectedValue,
          buyer_name: name.trim(),
          buyer_email: email.trim(),
          buyer_message: message.trim() || '',
          return_url: `${window.location.origin}/payment-success`,
          cancel_url: `${window.location.origin}/payment-cancel`,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Webhook fehlgeschlagen (${response.status}): ${errorText || 'Unbekannter Fehler'}`);
      }

      // Parse JSON response
      let raw: unknown;
      try {
        raw = await response.json();
      } catch (parseError) {
        console.error('[PayPal Gutschein] Invalid JSON response:', parseError);
        throw new Error('PayPal checkout konnte nicht initialisiert werden. Ungültige Antwort vom Server.');
      }

      const data = asRecord(raw);

      // Log full response for debugging
      console.log('[PayPal Gutschein] Webhook response:', data);

      // Extract PayPal approval URL - try multiple possible keys
      const approvalUrl = data.approval_url || data.approve || data.paypalLink;

      // Validate approval URL
      if (!approvalUrl || typeof approvalUrl !== 'string' || approvalUrl.trim() === '') {
        console.error('[PayPal Gutschein] No valid approval URL found in response:', {
          raw_response: data,
          checked_keys: ['approval_url', 'approve', 'paypalLink']
        });
        throw new Error('PayPal checkout konnte nicht initialisiert werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.');
      }

      // Log success
      console.log('[PayPal Gutschein] Order created successfully:', {
        order_id: data.id || 'unknown',
        approve_url: approvalUrl,
        amount: selectedValue
      });

      // Redirect to PayPal
      window.location.href = approvalUrl;

    } catch (err) {
      console.error('[PayPal Gutschein] Error:', err);
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
      setIsLoading(false);
    }
  };

  const handleStripePayment = async () => {
    if (!isFormValid) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('https://n8n.cogniiq.co/webhook/giftcard-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          value: selectedValue,
          name: name.trim(),
          email: email.trim(),
          message: message.trim() || '',
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Checkout fehlgeschlagen (${response.status}): ${text || 'Unbekannter Fehler'}`);
      }

      const data = await response.json();
      const checkoutUrl = data.checkoutUrl || data.url;

      if (!checkoutUrl || typeof checkoutUrl !== 'string') {
        throw new Error('Keine Checkout-URL erhalten. Bitte versuche es erneut.');
      }

      window.location.href = checkoutUrl;

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
      setIsLoading(false);
    }
  };

  if (purchasedCode) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 sm:p-12 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="w-10 h-10 text-green-600" />
          </motion.div>

          <h3 className="text-3xl font-semibold text-gray-900 mb-3">
            Gutschein erfolgreich erstellt
          </h3>
          <p className="text-[color:var(--svh-ink-2)] mb-8">
            Dein Code wurde an <strong>{email}</strong> gesendet
          </p>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 mb-6 border border-gray-200">
            <p className="text-[13px] text-[color:var(--svh-ink-2)] mb-2 font-semibold">Dein Gutscheincode</p>
            <div className="flex items-center justify-center gap-3 mb-4">
              <code className="text-2xl sm:text-3xl font-mono font-bold text-gray-900 tracking-wider">
                {purchasedCode}
              </code>
            </div>
            <button
              onClick={handleCopyCode}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 rounded-xl text-gray-700 font-medium transition-all duration-200 shadow-sm hover:shadow"
            >
              {copiedCode ? (
                <>
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Kopiert!</span>
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  <span>Code kopieren</span>
                </>
              )}
            </button>
          </div>

          <div className="space-y-3 text-[14px] text-[color:var(--svh-ink-2)]">
            <p className="flex items-start gap-2">
              <Mail className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <span>Eine Bestätigung wurde an deine E-Mail-Adresse gesendet</span>
            </p>
            <p className="flex items-start gap-2">
              <Gift className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <span>Gutschein ist sofort einsetzbar für Padel & Tennis Buchungen</span>
            </p>
          </div>

          <button
            onClick={() => {
              setPurchasedCode(null);
              setSelectedValue(null);
              setName('');
              setEmail('');
              setMessage('');
              setError('');
            }}
            className="mt-8 text-gray-600 hover:text-gray-900 font-medium transition-colors"
          >
            Weiteren Gutschein kaufen
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto svh-card svh-card-lg svh-card-raised p-6 sm:p-10">
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        {gutscheinOptions.map((option) => (
          <motion.button
            key={option.value}
            onClick={() => setSelectedValue(option.value)}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.98 }}
            className={`relative p-6 rounded-[16px] border-2 transition-all duration-300 text-left ${
              selectedValue === option.value
                ? 'border-[color:var(--svh-crest)] bg-[color:var(--svh-crest-wash)] shadow-[var(--shadow-md)]'
                : 'border-[color:var(--svh-border)] bg-white hover:border-gray-300 hover:shadow-[var(--shadow-md)]'
            }`}
          >
            <AnimatePresence>
              {selectedValue === option.value && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="absolute top-4 right-4 w-6 h-6 bg-[color:var(--svh-crest)] rounded-full flex items-center justify-center"
                >
                  <Check className="w-4 h-4 text-white" />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="svh-display-lg mb-4" style={{ fontSize: '46px' }}>{option.value}€</div>

            <div className="space-y-2">
              {option.benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </motion.button>
        ))}
      </div>

      <motion.div
        layout
        className="bg-white rounded-[24px] shadow-[0_2px_24px_rgba(0,0,0,0.06)] border border-[color:var(--svh-border)] p-7 sm:p-9"
      >
        <div className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-[color:var(--svh-crest)]">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Max Mustermann"
                className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all outline-none text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              E-Mail <span className="text-[color:var(--svh-crest)]">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="max@example.com"
                className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all outline-none text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Nachricht (optional)
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Persönliche Nachricht für den Beschenkten..."
                rows={4}
                className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all outline-none text-gray-900 placeholder-gray-400 resize-none"
              />
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm"
            >
              {error}
            </motion.div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleStripePayment}
              disabled={!isFormValid || isLoading}
              className={`w-full py-4 px-6 rounded-[16px] font-semibold text-white transition-all duration-300 text-[13px] tracking-wide ${
                !isFormValid || isLoading
                  ? 'bg-gray-200 cursor-not-allowed text-gray-400'
                  : 'bg-gray-900 hover:bg-gray-800 shadow-sm hover:shadow-md'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Wird verarbeitet...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  <span>Mit Karte / Apple Pay bezahlen</span>
                </span>
              )}
            </button>

            <button
              onClick={handlePayPalCheckout}
              disabled={!isFormValid || isLoading}
              className={`w-full py-4 px-6 rounded-[16px] font-semibold text-white transition-all duration-300 text-[13px] tracking-wide ${
                !isFormValid || isLoading
                  ? 'bg-gray-200 cursor-not-allowed text-gray-400'
                  : 'bg-[#0070BA] hover:bg-[#005EA6] shadow-sm'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Weiterleitung zu PayPal...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.905 9.5c.21-1.337.11-2.246-.36-3.13C19.698 5.076 18.171 4.5 15.875 4.5H8.042c-.51 0-.943.37-1.022.87L4.5 19.5h4.375l1.096-6.875h2.904c4.751 0 7.596-2.283 8.03-6.125zM9.931 9.5l.594-3.75h3.35c1.288 0 2.003.197 2.375.625.298.344.344.862.188 1.625-.297 1.875-1.594 2.5-3.906 2.5H9.931z"/>
                  </svg>
                  <span>Mit PayPal bezahlen</span>
                </span>
              )}
            </button>
          </div>

          <div className="flex items-center justify-center gap-6 text-xs text-gray-500 pt-2">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              Sichere Zahlung
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="w-4 h-4" />
              Code sofort verfügbar
            </span>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2">
            Gutscheine sind nicht bar auszahlbar. Einlösbar gemäß unseren Bedingungen.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default GutscheinKaufen;
